import React from 'react';
import { usePageMeta, useStructuredData } from '../Hooks';
import './Pages.css';

const faqs = [
  {
    question: 'Should I invest in a pension or a Stocks and Shares ISA?',
    answer:
      'Both have advantages. Pensions benefit from tax relief (20–45%) and employer contributions under auto-enrolment, but your money is locked away until at least age 57. A Stocks and Shares ISA offers no upfront tax relief but growth and withdrawals are completely tax-free and you can access your money at any time. Many UK investors use both — maximising employer pension contributions first, then using an ISA for additional flexibility.',
  },
  {
    question: 'How does pension tax relief work in the UK?',
    answer:
      'The government tops up your pension contributions at your marginal Income Tax rate. Basic rate taxpayers (20%) receive 20p for every 80p contributed, making a £100 pension contribution cost just £80. Higher rate taxpayers (40%) can claim additional relief through their self-assessment, reducing the cost to £60. Additional rate taxpayers (45%) can reduce the cost to £55. The annual pension allowance is £60,000 (2024/25), though this may be tapered for high earners.',
  },
  {
    question: 'What is the difference between a SIPP and a workplace pension?',
    answer:
      'A SIPP (Self-Invested Personal Pension) gives you full control over your investments, including access to low-cost global index funds like the Vanguard FTSE All-World or iShares S&P 500. A workplace pension is set up by your employer — it includes employer contributions under auto-enrolment (minimum 3%) but may have limited fund choices and higher charges. You can contribute to both simultaneously.',
  },
  {
    question: 'How much can I invest in a Stocks and Shares ISA each year?',
    answer:
      'The annual ISA allowance is £20,000 per tax year (2024/25). All growth, dividends, and withdrawals within an ISA are completely free of Income Tax and Capital Gains Tax. Unlike a pension, there is no employer contribution or government top-up, but you can withdraw at any time without tax penalties.',
  },
  {
    question: 'What is a safe withdrawal rate in retirement?',
    answer:
      'The "4% rule" originated from US research suggesting a 4% annual withdrawal from a diversified portfolio has historically lasted 30+ years. In the UK context, lower expected bond yields suggest some advisers recommend 3–3.5% for greater safety. With a pension you can choose flexible income drawdown, adjusting withdrawals based on fund performance and spending needs.',
  },
  {
    question: 'What average return should I assume for a UK index fund?',
    answer:
      'Global index funds tracking the FTSE All-World or MSCI World have historically returned around 8–10% per year before charges. After a typical low-cost index fund charge of 0.2–0.5%, net returns have averaged roughly 7–9%. Past performance is not a guarantee of future results, and returns vary significantly year to year.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(f => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const About: React.FC = () => {
  usePageMeta(
    'About | UK Pension vs Index Fund Calculator',
    'Learn how our free UK pension calculator works, how pension tax relief and auto-enrolment affect your retirement, and how a SIPP or Stocks and Shares ISA compares to a workplace pension.'
  );
  useStructuredData(faqSchema);

  return (
    <div className="page-container">
      <h1>About the UK Pension vs Index Fund Calculator</h1>
      <div className="page-content">
        <p>
          This free calculator helps UK investors compare two of the most popular retirement
          strategies: investing through a pension scheme (workplace pension or SIPP) versus building
          wealth in a Stocks and Shares ISA using low-cost index funds such as the Vanguard FTSE
          All-World or iShares S&P 500.
        </p>

        <h2>How the pension calculation works</h2>
        <p>
          The calculator models compound growth on your pension contributions, applying your chosen
          annual return net of the 0.75% annual management charge typical of UK workplace pensions.
          It adds government tax relief at your marginal rate (20%, 40%, or 45%), employer
          contributions under auto-enrolment, and annual contribution increases to simulate wage
          growth over your career.
        </p>

        <h2>How the index fund calculation works</h2>
        <p>
          For a Stocks and Shares ISA or general investment account, the calculator applies compound
          growth at your chosen return (global index funds have historically averaged 8–10% before
          charges), deducts a 0.2% annual charge typical of low-cost UK index fund platforms, and —
          for non-ISA accounts — models Capital Gains Tax using the annual CGT allowance.
        </p>

        <h2>Retirement drawdown projection</h2>
        <p>
          After your accumulation phase, the calculator projects your fund from retirement to age
          100 using flexible income drawdown at your chosen withdrawal rate. The 4% rule is a
          commonly cited starting point, but you can adjust this to model more conservative or
          aggressive drawdown strategies. All figures can be viewed in nominal terms or
          inflation-adjusted today's money.
        </p>

        <h2>UK-specific features</h2>
        <ul>
          <li>Pension tax relief at 20%, 40%, or 45% marginal rate</li>
          <li>Employer contributions and auto-enrolment modelling</li>
          <li>Stocks and Shares ISA tax-free growth (£20,000 annual allowance, 2024/25)</li>
          <li>Capital Gains Tax modelling for non-ISA index fund accounts</li>
          <li>UK State Pension age context (currently 66, rising to 67 by 2028)</li>
          <li>Inflation adjustment using the Bank of England 2% target or your own assumption</li>
          <li>Separate drawdown rates for pension and ISA in retirement</li>
        </ul>

        <h2>Frequently asked questions</h2>
        <div className="faq-list">
          {faqs.map((faq, i) => (
            <details key={i} className="faq-item">
              <summary className="faq-question">{faq.question}</summary>
              <p className="faq-answer">{faq.answer}</p>
            </details>
          ))}
        </div>

        <h2>Disclaimer</h2>
        <p>
          This tool is for educational and informational purposes only. Projections are estimates
          based on the inputs you provide and assumed rates of return — they are not a guarantee of
          future performance. Tax rules, allowances, and legislation may change. Please consult a
          qualified financial adviser regulated by the FCA before making investment or pension
          decisions.
        </p>
      </div>
    </div>
  );
};

export default About;
